import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { EnrollmentBadges } from "@/components/ui/benefits/EnrollmentBadges";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { MetricCard } from "@/components/dashboard/MetricCard";
import {
  Heart,
  DollarSign,
  Calendar,
  Clock,
  Users,
  FileText,
  Shield,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  Plus,
  Eye,
  ChevronRight,
  Info,
  X
} from "lucide-react";
import { Link } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { useEnrollmentStatus } from "@/hooks/useEnrollmentStatus";
import { useClientPlans } from "@/hooks/useClientPlans";
import { isHealthOrMedicalPlan } from "@shared/schema";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatDateDisplay, parseDateLocal } from "@/lib/utils";
import { groupEnrollmentsByPlan } from "@/lib/enrollmentGrouping";
import { isCurrentlyEffectiveEnrollment, countInForcePlans } from "@/lib/enrollmentFilters";
import { pluralize } from "@/lib/pluralize";
import { getOpenEnrollmentPlanYear } from "@/lib/openEnrollmentPlanYear";
import {
  formatPremiumWithPayCycle,
  getPayCycleLabel,
  getPayCycleLabelCapitalized,
  calculatePerPaycheckAmount,
  hasNonMonthlyPayCycle,
} from "@/lib/payrollUtils";

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const [, navigate] = useLocation();

  // Fetch employee data to check onboarding status
  const { data: employee } = useQuery<any>({
    queryKey: ["/api/employees/me"],
  });

  // Check if onboarding is complete, redirect if not
  useEffect(() => {
    if (employee?.id && !employee.onboardingCompletedAt) {
      navigate("/onboarding");
    }
  }, [employee?.id, employee?.onboardingCompletedAt, navigate]);
  const { data: ptoBalances } = useQuery({ queryKey: ["/api/time-off/employee/balances"] });
  const { data: ptoRequests } = useQuery({ queryKey: ["/api/time-off/employee/requests"] });
  const { data: acaStatus } = useQuery({ queryKey: ["/api/employees/aca/status"] });

  const {
    enrollmentState,
    hasCompletedOpenEnrollment, oeWindow,
    newHireWindow,
  } = useEnrollmentStatus(user?.clientId, user?.employeeId);

  const daysRemainingInEnrollmentPeriod = newHireWindow?.daysRemaining || 0;

  // Track if new hire enrollment banner has been dismissed
  const [newHireBannerDismissed, setNewHireBannerDismissed] = useState(false);

  // Check localStorage for banner dismissal when user is available
  useEffect(() => {
    if (user?.employeeId) {
      const dismissed = localStorage.getItem(`new_hire_banner_dismissed_${user.employeeId}`) === 'true';
      setNewHireBannerDismissed(dismissed);
    }
  }, [user?.employeeId]);

  // Handle dismissing the new hire enrollment banner
  const handleDismissNewHireBanner = () => {
    if (user?.employeeId) {
      localStorage.setItem(`new_hire_banner_dismissed_${user.employeeId}`, 'true');
    }
    setNewHireBannerDismissed(true);
  };

  // Fetch employee record for premium calculations
  const { data: employeeRecord } = useQuery<any>({
    queryKey: ["/api/employees/me"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/employees/me");
      return response.json();
    },
  });

  // Fetch all plans for enrichment - use shared hook
  const { allPlans: employeeAllPlans = [], planYears: employeePlanYears = [] } = useClientPlans(user?.clientId);

  // Year the OE enrolls into (its plans' start), not the year the window opens.
  const oePlanYear = getOpenEnrollmentPlanYear(oeWindow, employeePlanYears as any[]);

  // Fetch dependents for coverage calculations (same as Benefits.tsx)
  const { data: dependents = [] } = useQuery({
    queryKey: ["/api/employees", user?.employeeId, "dependents"],
    enabled: Boolean(user?.employeeId),
    queryFn: async () => {
      const res = await fetch(`/api/employees/${user?.employeeId}/dependents`);
      if (!res.ok) return [] as any[];
      return res.json();
    },
  });

  // Fetch current benefit enrollments from DB and enrich with plan details and computed premiums (same as Benefits.tsx)
  const { data: enrollments = [] } = useQuery({
    queryKey: [
      "/api/employees",
      user?.employeeId,
      "enrollments-enriched-dashboard-v4", // Updated key to bust cache
      user?.clientId,
      employeeAllPlans.length, // Re-run when plans data changes
      employeeRecord?.dateOfBirth, // Re-run when employee DOB is loaded (needed for age-based calculations)
    ],
    enabled: Boolean(user?.employeeId && user?.clientId && employeeRecord && employeeAllPlans.length > 0),
    queryFn: async () => {
      // Consume SERVER-computed premiums (withComputedPremiums) rather than
      // recomputing client-side — the client can't resolve the enrollment's
      // snapshotted eligibility-group contribution rule set, so multi-group
      // clients were mis-priced. /benefits nests .plan and the computed fields.
      const res = await fetch(`/api/employees/${user?.employeeId}/benefits`);
      if (!res.ok) return [] as any[];
      const rows = (await res.json()) as any[];

      // Group rows by planId using shared utility
      const byPlan = groupEnrollmentsByPlan(rows || []);

      const results: any[] = [];
      for (const planId of Array.from(byPlan.keys())) {
        const group = byPlan.get(planId)!;
        const base = group.base;
        const depIds = group.dependentIds;
        if (!base) continue;
        const plan = (base as any)?.plan
          || (employeeAllPlans as any[]).find((p: any) => p.id === planId);
        if (!plan) {
          console.warn(`Plan with ID ${planId} not found in employeeAllPlans`);
          continue;
        }
        const finalPremium = Number(
          (base as any)?.employeePremium ?? (base as any)?.premium?.employeePremium ?? 0
        );

        results.push({
          id: (base as any)?.id,
          planId,
          planName: (plan as any)?.name,
          planType: (plan as any)?.type,
          coverageLevel: (base as any)?.coverageTier,
          monthlyPremium: finalPremium,
          employeeContribution: finalPremium,
          effectiveDate: (base as any)?.effectiveDate,
          endDate: (base as any)?.endDate,
          waivedCoverage: (base as any)?.waivedCoverage,
          status: (base as any)?.status,
          enrolledDependents: depIds,
        });
      }
      return results;
    }
  });

  // Ensure we have arrays, even if queries are loading
  const ptoRequestsArray = Array.isArray(ptoRequests) ? ptoRequests : [];
  const enrollmentsArray = Array.isArray(enrollments) ? enrollments : [];

  // Convert new time-off balances array to keyed format the UI expects
  const safePtoBalance = useMemo(() => {
    if (!Array.isArray(ptoBalances) || ptoBalances.length === 0) return null;
    const findByType = (type: string) => (ptoBalances as any[]).find((b: any) => b.policyType === type);
    const vacation = findByType("vacation");
    const sick = findByType("sick");
    const personal = findByType("personal");
    return {
      available: {
        vacation: vacation?.available || 0,
        sick: sick?.available || 0,
        personal: personal?.available || 0,
      },
      total: {
        allocated: (ptoBalances as any[]).reduce((sum: number, b: any) => sum + (b.allocated || 0), 0),
        available: (ptoBalances as any[]).reduce((sum: number, b: any) => sum + (b.available || 0), 0),
      },
    };
  }, [ptoBalances]);
  const safeAcaStatus = acaStatus && typeof acaStatus === 'object' ? acaStatus as any : null;

  // Calculate upcoming PTO
  const upcomingPTO = ptoRequestsArray.filter((request: any) =>
    request.status === "approved" && (parseDateLocal(request.startDate) ?? new Date(0)) > new Date()
  );

  // Calculate pending requests
  const pendingRequests = ptoRequestsArray.filter((request: any) => 
    request.status === "pending"
  );

  // Calculate total monthly premiums using the enriched data with proper premium calculations
  // Filter to only active/pending enrollments that are not waived or ended (consistent with Benefits tab)
  const activeEnrollments = enrollmentsArray.filter(isCurrentlyEffectiveEnrollment);
  const totalPremium = activeEnrollments.reduce((sum: number, enrollment: any) =>
    sum + (Number(enrollment.monthlyPremium) || 0), 0
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">
            Hi {employee?.firstName || user?.firstName || 'there'}
            {employee?.position && (
              <span className="text-xl font-normal text-muted-foreground ml-2">
                / {employee.position}
              </span>
            )}
          </h1>
          <p className="text-muted-foreground">
            Welcome back! Here's an overview of your benefits and time off.
          </p>
        </div>
        <Badge variant="secondary" className="text-sm">
          Employee Portal
        </Badge>
      </div>

      {/* ONB-10: Getting Started card for first-time employees with no
          enrollments yet. Hides as soon as any enrollment exists. */}
      {enrollmentsArray.length === 0 && (
        <Card className="border-l-4 border-blue-500 bg-blue-50/60 dark:bg-blue-950/30">
          <CardContent className="pt-6 pb-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-500 rounded-lg shrink-0">
                <Info className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold mb-1">Getting Started</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Welcome! Here's how to set up your benefits in three steps.
                </p>
                <ol className="space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-semibold shrink-0">
                      {dependents && dependents.length > 0 ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : (
                        "1"
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div>
                          <p className="font-medium text-sm flex items-center gap-1.5">
                            <Users className="h-4 w-4" />
                            Add your dependents
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Spouse, children, or domestic partners to cover under your plans.
                          </p>
                        </div>
                        <Link href="/profile">
                          <Button variant="outline" size="sm">
                            {dependents && dependents.length > 0 ? "Manage" : "Add dependents"}
                            <ChevronRight className="ml-1 h-3 w-3" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-semibold shrink-0">
                      2
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div>
                          <p className="font-medium text-sm flex items-center gap-1.5">
                            <Heart className="h-4 w-4" />
                            Choose a health plan
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Pick the medical coverage that fits your needs.
                          </p>
                        </div>
                        <Link href="/benefits">
                          <Button variant="outline" size="sm">
                            Browse health plans
                            <ChevronRight className="ml-1 h-3 w-3" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-semibold shrink-0">
                      3
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div>
                          <p className="font-medium text-sm flex items-center gap-1.5">
                            <Shield className="h-4 w-4" />
                            Review dental &amp; vision
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Optional but usually low-cost; worth reviewing before you finish.
                          </p>
                        </div>
                        <Link href="/benefits">
                          <Button variant="outline" size="sm">
                            Review options
                            <ChevronRight className="ml-1 h-3 w-3" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Open Enrollment Banner */}
      {enrollmentState === "open_enrollment" && (
        <Card className="border-l-4 border-blue-500 bg-blue-50 animate-[pulse-bg_2s_ease-in-out_infinite]">
          <style>{`
            @keyframes pulse-bg {
              0%, 100% { background-color: rgb(239 246 255); }
              50% { background-color: rgb(219 234 254); }
            }
          `}</style>
          <CardContent className="pt-8 pb-8">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-blue-500 rounded-lg">
                  <Info className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    {hasCompletedOpenEnrollment
                      ? "Open Enrollment - Elections Submitted"
                      : "Open Enrollment is Now Active!"}
                  </h3>
                  <p className="text-base text-gray-700 leading-relaxed">
                    {hasCompletedOpenEnrollment
                      ? `You've completed your benefit elections for ${oePlanYear ?? 'the upcoming year'}. You can still modify your elections before the deadline.`
                      : `Make your benefit elections for ${oePlanYear ?? 'the upcoming year'}. Open Enrollment ends on ${oeWindow?.endDate ? formatDateDisplay(oeWindow.endDate) : 'the deadline'}.`}
                  </p>
                  <div className="mt-3 flex items-center space-x-4 text-sm">
                    <div className="flex items-center text-gray-700 font-medium">
                      <Clock className="h-4 w-4 mr-1.5" />
                      <span>
                        {oeWindow?.startDate && oeWindow?.endDate
                          ? `${formatDateDisplay(oeWindow.startDate)} - ${formatDateDisplay(oeWindow.endDate)}`
                          : 'Active Period'}
                      </span>
                    </div>
                    {hasCompletedOpenEnrollment && (
                      <div className="flex items-center text-green-600 font-medium">
                        <CheckCircle className="h-4 w-4 mr-1.5" />
                        <span>Elections Confirmed</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="ml-4">
                <Link href="/benefits">
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700 font-semibold">
                    {hasCompletedOpenEnrollment ? "Modify Elections" : "Begin Open Enrollment"}
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Initial Enrollment Period Banner (30 days from hire) */}
      {enrollmentState === "new_hire" && !newHireBannerDismissed && (
        <Card className="border-l-4 border-blue-500 bg-blue-50 animate-[pulse-bg-blue_2s_ease-in-out_infinite] relative">
          <style>{`
            @keyframes pulse-bg-blue {
              0%, 100% { background-color: rgb(239 246 255); }
              50% { background-color: rgb(219 234 254); }
            }
          `}</style>
          {/* Dismiss button */}
          <button
            onClick={handleDismissNewHireBanner}
            className="absolute top-3 right-3 p-1 rounded-full hover:bg-blue-200 transition-colors text-gray-500 hover:text-gray-700"
            title="Dismiss (if already enrolled in benefits)"
            aria-label="Dismiss new hire enrollment banner"
          >
            <X className="h-5 w-5" />
          </button>
          <CardContent className="pt-8 pb-8 pr-12">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-blue-500 rounded-lg">
                  <Info className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    New Hire Benefits Enrollment Period
                  </h3>
                  <p className="text-base text-gray-700 leading-relaxed">
                    Welcome! As a new hire, you have until {newHireWindow?.windowEndDate ? (parseDateLocal(newHireWindow.windowEndDate) ?? new Date()).toLocaleDateString() : 'your deadline'} to enroll in benefits.
                    {enrollmentsArray.length > 0
                      ? " You've already started enrolling, but you can still add or modify your elections before the deadline."
                      : " Take advantage of this time to review available plans and enroll in the benefits that work best for you and your family."}
                  </p>
                  <div className="mt-3 flex items-center space-x-4 text-sm">
                    <div className="flex items-center text-gray-700 font-medium">
                      <Calendar className="h-4 w-4 mr-1.5" />
                      <span>
                        Hire Date: {employee?.hireDate ? formatDateDisplay(employee.hireDate) : 'N/A'}
                      </span>
                    </div>
                    <div className="flex items-center text-gray-700 font-medium">
                      <Clock className="h-4 w-4 mr-1.5" />
                      <span className="font-semibold">
                        {daysRemainingInEnrollmentPeriod === 0
                          ? "Last day to enroll!"
                          : daysRemainingInEnrollmentPeriod === 1
                          ? "1 day remaining"
                          : `${daysRemainingInEnrollmentPeriod} days remaining`}
                      </span>
                    </div>
                    {enrollmentsArray.length > 0 && (
                      <div className="flex items-center text-green-600 font-medium">
                        <CheckCircle className="h-4 w-4 mr-1.5" />
                        <span>{enrollmentsArray.length} plan{enrollmentsArray.length !== 1 ? 's' : ''} enrolled</span>
                      </div>
                    )}
                  </div>
                  {/* <div className="mt-3 p-3 bg-blue-100 rounded-md border-l-2 border-blue-400">
                    <p className="text-sm text-blue-900 font-medium">
                      <strong className="font-semibold">Important:</strong> Coverage typically begins on the first day of the month following your enrollment.
                      Don't miss this opportunity to protect yourself and your family.
                    </p>
                  </div> */}
                  <p className="mt-2 text-xs text-gray-500 italic">
                    Already enrolled? Click the X to dismiss this banner.
                  </p>
                </div>
              </div>
              <div className="ml-4">
                <Link href="/benefits">
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700 font-semibold">
                    {enrollmentsArray.length > 0 ? "Modify Enrollment" : "Enroll Now"}
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Stats Overview */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          color="teal"
          title="Available PTO"
          icon={Clock}
          value={safePtoBalance?.total?.available || 0}
          subtitle={`${pluralize(safePtoBalance?.total?.available || 0, "day")} remaining`}
          progressValue={
            safePtoBalance?.total
              ? ((safePtoBalance.total.available || 0) /
                  (safePtoBalance.total.allocated || 1)) *
                100
              : 0
          }
        />

        <MetricCard
          color="green"
          title={
            hasNonMonthlyPayCycle(employee?.payCycle)
              ? `${getPayCycleLabelCapitalized(employee?.payCycle)} Deduction`
              : "Monthly Premium"
          }
          icon={DollarSign}
          value={
            <>
              $
              {hasNonMonthlyPayCycle(employee?.payCycle)
                ? calculatePerPaycheckAmount(
                    totalPremium,
                    employee?.payCycle,
                  ).toFixed(2)
                : totalPremium.toFixed(2)}
            </>
          }
          subtitle={
            hasNonMonthlyPayCycle(employee?.payCycle)
              ? `($${totalPremium.toFixed(2)}/month)`
              : "your contribution"
          }
        />

        <MetricCard
          color="blue"
          title="Active Benefits"
          icon={Shield}
          value={countInForcePlans(enrollmentsArray)}
          subtitle={`${pluralize(countInForcePlans(enrollmentsArray), "plan")} enrolled`}
        />

        <MetricCard
          color="purple"
          title="ACA Status"
          icon={CheckCircle}
          value={
            safeAcaStatus?.computed
              ? ({ affordable: "Affordable", not_affordable: "Not affordable", pending_data: "Pending", not_applicable: "N/A", mixed: "Mixed" } as Record<string, string>)[safeAcaStatus?.summary?.status] ?? "N/A"
              : "N/A"
          }
          subtitle={safeAcaStatus?.computed ? `${safeAcaStatus.summary?.fullTimeMonths ?? 0}/12 full-time months (${safeAcaStatus.taxYear})` : "compliance status"}
        />
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common tasks and shortcuts</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-6">
            <Link href="/pto">
              <Button variant="outline" className="w-full justify-start">
                <Calendar className="mr-2 h-4 w-4" />
                Request Time Off
              </Button>
            </Link>
            <Link href="/benefits">
              <Button variant="outline" className="w-full justify-start">
                <Plus className="mr-2 h-4 w-4" />
                Enroll in Benefits
              </Button>
            </Link>
            <Link href="/profile">
              <Button variant="outline" className="w-full justify-start">
                <Users className="mr-2 h-4 w-4" />
                Update Profile
              </Button>
            </Link>
            <Link href="/documents">
              <Button variant="outline" className="w-full justify-start">
                <FileText className="mr-2 h-4 w-4" />
                View Documents
              </Button>
            </Link>
            <Link href="/company">
              <Button variant="outline" className="w-full justify-start">
                <Users className="mr-2 h-4 w-4" />
                Company Directory
              </Button>
            </Link>
            <Link href="/reports">
              <Button variant="outline" className="w-full justify-start">
                <TrendingUp className="mr-2 h-4 w-4" />
                My Reports
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        {/* PTO Summary */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Time Off Summary</CardTitle>
              <CardDescription>Your PTO balance and recent activity</CardDescription>
            </div>
            <Link href="/pto">
              <Button variant="outline" size="sm">
                <Eye className="mr-2 h-4 w-4" />
                View All
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="space-y-4">
            <TooltipProvider>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-lg font-bold text-green-600">
                    {safePtoBalance ? safePtoBalance.available?.vacation || 0 : (
                      <Tooltip>
                        <TooltipTrigger>-</TooltipTrigger>
                        <TooltipContent>Configure a PTO policy to track vacation balances</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">Vacation</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-blue-600">
                    {safePtoBalance ? safePtoBalance.available?.sick || 0 : (
                      <Tooltip>
                        <TooltipTrigger>-</TooltipTrigger>
                        <TooltipContent>Configure a PTO policy to track sick balances</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">Sick</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-purple-600">
                    {safePtoBalance ? safePtoBalance.available?.personal || 0 : (
                      <Tooltip>
                        <TooltipTrigger>-</TooltipTrigger>
                        <TooltipContent>Configure a PTO policy to track personal balances</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">Personal</div>
                </div>
              </div>
            </TooltipProvider>

            {pendingRequests.length > 0 && (
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium mb-2 flex items-center">
                  <AlertCircle className="mr-2 h-4 w-4 text-yellow-500" />
                  Pending Requests ({pendingRequests.length})
                </h4>
                <div className="space-y-2">
                  {pendingRequests.slice(0, 2).map((request: any) => (
                    <div key={request.id} className="flex justify-between text-sm">
                      <span>{request.policyName || request.type} - {request.totalHours ? `${Math.ceil(request.totalHours / 8)} days` : `${request.totalDays} days`}</span>
                      <span className="text-muted-foreground">
                        {formatDateDisplay(request.startDate)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {upcomingPTO.length > 0 && (
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium mb-2 flex items-center">
                  <Clock className="mr-2 h-4 w-4 text-blue-500" />
                  Upcoming Time Off
                </h4>
                <div className="space-y-2">
                  {upcomingPTO.slice(0, 2).map((request: any) => (
                    <div key={request.id} className="flex justify-between text-sm">
                      <span>{request.policyName || request.type} - {request.totalHours ? `${Math.ceil(request.totalHours / 8)} days` : `${request.totalDays} days`}</span>
                      <span className="text-muted-foreground">
                        {formatDateDisplay(request.startDate)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Benefits Summary */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Benefits Overview</CardTitle>
              <CardDescription>Your current coverage and plans</CardDescription>
            </div>
            <Link href="/benefits">
              <Button variant="outline" size="sm">
                <Eye className="mr-2 h-4 w-4" />
                Manage
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="space-y-4">
            {activeEnrollments.length > 0 ? (
              <div className="space-y-3">
                {activeEnrollments.slice(0, 3).map((enrollment: any) => (
                  <div key={enrollment.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`p-1 rounded ${
                        isHealthOrMedicalPlan(enrollment.planType) ? 'bg-red-100' :
                        enrollment.planType === 'dental' ? 'bg-blue-100' :
                        enrollment.planType === 'vision' ? 'bg-green-100' :
                        'bg-purple-100'
                      }`}>
                        <Heart className={`h-4 w-4 ${
                          isHealthOrMedicalPlan(enrollment.planType) ? 'text-red-600' :
                          enrollment.planType === 'dental' ? 'text-blue-600' :
                          enrollment.planType === 'vision' ? 'text-green-600' :
                          'text-purple-600'
                        }`} />
                      </div>
                      <div>
                        <div className="font-medium">{enrollment.planName}</div>
                        <div className="text-sm text-muted-foreground">
                          ${hasNonMonthlyPayCycle(employee?.payCycle)
                            ? calculatePerPaycheckAmount(Number(enrollment.monthlyPremium) || Number(enrollment.employeeContribution) || 0, employee?.payCycle).toFixed(2)
                            : (Number(enrollment.monthlyPremium) || Number(enrollment.employeeContribution) || 0).toFixed(2)}/{getPayCycleLabel(employee?.payCycle)}
                        </div>
                      </div>
                    </div>
                    {/* Shared badge: a terminated row whose endDate is still ahead shows
                        Active + "Terminating <date>", matching the Active Benefits tile. */}
                    <EnrollmentBadges enrollment={enrollment} className="justify-end" />
                  </div>
                ))}
                
                <div className="border-t pt-3">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">
                      Total {hasNonMonthlyPayCycle(employee?.payCycle)
                        ? getPayCycleLabelCapitalized(employee?.payCycle)
                        : "Monthly"} Cost:
                    </span>
                    <span className="text-lg font-bold">
                      ${hasNonMonthlyPayCycle(employee?.payCycle)
                        ? calculatePerPaycheckAmount(totalPremium, employee?.payCycle).toFixed(2)
                        : totalPremium.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <Shield className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No active benefits enrolled</p>
                <Link href="/benefits">
                  <Button size="sm" className="mt-2">
                    Explore Plans
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity or Important Notices */}
      <Card>
        <CardHeader>
          <CardTitle>Important Information</CardTitle>
          <CardDescription>Updates and reminders for your benefits</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {safeAcaStatus?.isEligible && (
              <div className="flex items-start space-x-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <div className="font-medium text-green-900">ACA Compliant</div>
                  <div className="text-sm text-green-700">
                    Your current benefits meet ACA minimum value and affordability standards.
                  </div>
                </div>
              </div>
            )}
            
            {pendingRequests.length > 0 && (
              <div className="flex items-start space-x-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <div className="font-medium text-yellow-900">Pending PTO Requests</div>
                  <div className="text-sm text-yellow-700">
                    You have {pendingRequests.length} time off request(s) awaiting approval.
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-start space-x-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <TrendingUp className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <div className="font-medium text-blue-900">Benefits Review</div>
                <div className="text-sm text-blue-700">
                  Review your benefits annually during open enrollment to ensure they meet your needs.
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}